import React, { useState, useEffect, useCallback } from 'react';
import { FormField } from '../common/form-field.component';
import { LogoUpload } from '../common/logo-upload.component';
import { InvoicePreview } from './invoice-preview.component';
import { OTPModal } from '../common/otp-modal.component';
import { ConfirmationModal } from '../common/confirmation-modal.component';
import { useInvoice } from '../../hooks/useInvoice';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrganization } from '../../features/organization/organization.slice';
import { RootState, AppDispatch } from '../../store';
import './invoice-config.style.scss';
import { toast } from 'react-toastify';
import axios from 'axios';
import { getItem } from '../../helper/localstorage.helper';
import { updateUserOnboardingService } from '../../features/userOnboarding/userOnboarding.slice';
import { getUserOnboarding } from '../../../redux/auth/authSlice';
import { useTranslation } from 'react-i18next';
import apiCall from '../../../api';
import { isGuestInvozioEmail } from '../../helper/common.helper';

export interface InvoiceConfigData {
  organizationName: string;
  logo: File | null;
  bankName: string;
  accountNumber: string;
  clearingNumber: string;
  ibanNumber?: string;
  bicNumber?: string;
  otherBankName?: string;
}

export interface InvoiceConfigProps {
  initialData?: Partial<InvoiceConfigData>;
  onChange?: (data: InvoiceConfigData) => void;
  onSubmit?: (data: InvoiceConfigData) => void;
  onSkip?: () => void;
  showHeader?: boolean;
  showPreview?: boolean;
  submitButtonText?: string;
  className?: string;
  validationRules?: Partial<Record<keyof InvoiceConfigData, (value: any) => string | undefined>>;
  readOnly?: boolean;
}

export const InvoiceConfig: React.FC<InvoiceConfigProps> = ({
  initialData = {},
  onChange,
  onSubmit,
  showHeader = true,
  showPreview = false,
  submitButtonText = "SAVE_AND_PROCEED",
  className = '',
  validationRules = {},
  readOnly = false
}) => {
  const {
    bankDetails,
    loading,
    error,
    message,
    otpLoading,
    saveLoading,
    clearError,
    clearMessage,
    generateOTP,
    saveBankDetails,
    loadBankDetails
  } = useInvoice();

  const dispatch: AppDispatch = useDispatch();
  const {
          personalData
      } = useSelector((state: any) => state.profileSettings);
const { data: organizationData, loading: orgLoading, error: orgError } = useSelector((state: RootState) => state.organization);
const [formData, setFormData] = useState<InvoiceConfigData>({
    organizationName: initialData?.signUpUser?.organizationName || '',
    logo: initialData.logo || null,
    bankName: initialData.bankName || '',
    accountNumber: initialData.accountNumber || '',
    clearingNumber: initialData.clearingNumber || '',
    ibanNumber: initialData.ibanNumber || '',
    bicNumber: initialData.bicNumber || '',
    otherBankName: initialData.otherBankName || '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof InvoiceConfigData, string>>>({});
  const { t } = useTranslation();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpCounter, setOtpCounter] = useState(300);
  const [otpResetTrigger, setOtpResetTrigger] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<InvoiceConfigData | null>(null);
  useEffect(() => {
    if (bankDetails && bankDetails.length > 0 && !formData.bankName) {
      const firstBank = bankDetails[0];
      const newFormData = {
        ...formData,
        organizationName: firstBank?.signUpUser?.organizationName ?? '',
        bankName: firstBank.bank,
        accountNumber: firstBank.accountNumber || '',
        clearingNumber: firstBank.clearingNumber || '',
        ibanNumber: firstBank.ibanNumber || '',
        bicNumber: firstBank.bicNumber || '',

      };

      setFormData(newFormData);
      onChange?.(newFormData);
    }
  }, [bankDetails, formData.bankName, onChange]);

  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      const newFormData = {
        organizationName: formData?.signUpUser?.organizationName || '',
        logo: initialData.logo || formData.logo,
        bankName: initialData.bankName || formData.bankName,
        accountNumber: initialData.accountNumber || formData.accountNumber,
        clearingNumber: initialData.clearingNumber || formData.clearingNumber,
        ibanNumber: initialData.ibanNumber || formData.ibanNumber,
        bicNumber: initialData.bicNumber || formData.bicNumber,
        otherBankName: initialData.otherBankName || formData.otherBankName,
      };
      if (bankDetails && initialData.bankName) {
        const matchedBank = findBankByIdOrName(initialData.bankName);

        if (matchedBank) {
          newFormData.bankName = matchedBank.bank;
          newFormData.accountNumber = newFormData.accountNumber || matchedBank.accountNumber || '';
          newFormData.clearingNumber = newFormData.clearingNumber || matchedBank.clearingNumber || '';
          newFormData.ibanNumber = newFormData.ibanNumber || matchedBank.ibanNumber || '';
          newFormData.bicNumber = newFormData.bicNumber || matchedBank.bicNumber || '';
        } else {
          newFormData.bankName = initialData.bankName;
        }
      }

      setFormData(newFormData);
      onChange?.(newFormData);
    }
  }, [initialData, bankDetails]);

  const defaultValidationRules = {
    bankName: (value: string) => !value ? (t('BANK_NAME_REQUIRED') as string) : undefined,
    accountNumber: (value: string) => {
      if (isForeignBank(formData.bankName)) return undefined;
      if (!value) return t('ACCOUNT_NUMBER_REQUIRED');
      if (!/^\d{6,10}$/.test(value.replace(/\s/g, ''))) {
        return (t('ACCOUNT_NUMBER_INVALID') as string);
      }
      return undefined;
    },
    clearingNumber: (value: string) => {
      if (isForeignBank(formData.bankName)) return undefined;
      if (!value) return t('CLEARING_NUMBER_REQUIRED');
      if (!/^\d{4,5}$/.test(value.replace(/\s/g, ''))) {
        return t('CLEARING_NUMBER_INVALID');
      }
      return undefined;
    },
    ibanNumber: (value: string) => {
      if (!isForeignBank(formData.bankName)) return undefined;
      if (!value) return 'IBAN number is required';
      return undefined;
    },
    bicNumber: (value: string) => {
      if (!isForeignBank(formData.bankName)) return undefined;
      if (!value) return 'BIC number is required';
      return undefined;
    }
  };

  const isForeignBank = (bankName: string) => {
    return ["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName);
  };

  const uploadLogoToAPI = async (logoFile: File): Promise<boolean> => {
    if (!logoFile) return true;

    try {
      const baseUrl = (import.meta as any).env.VITE_API_URL;
      const token = localStorage.getItem('token');

      const formDataLogo = new FormData();
      formDataLogo.append('file', logoFile);

      const response = await apiCall.post(
        `${baseUrl}/signup/invoice-logo`,
        formDataLogo,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response?.status === 200 || response?.status === 201) {
        return true;
      } else {
        console.error('Logo upload failed:', response);
        toast.error(t('FAILED_TO_UPLOAD_LOGO') as string);
        return false;
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || (t('FAILED_TO_UPLOAD_LOGO') as string);
      console.error('Logo upload error:', error);
      toast.error(errorMessage);
      return false;
    }
  };

  const handleGenerateOTP = async () => {
    try {
      const language = getItem("i18nextLng") || 'en';
      await generateOTP(language);
      setShowOTPModal(true);
      setOtpCounter(300);
      setOtpResetTrigger(!otpResetTrigger); // Trigger reset
    } catch (error) {
      console.error('Failed to generate OTP:', error);
    }
  };

  const handleOTPSubmit = async (otp: string) => {
    if (!pendingFormData) return;

    try {
      if (pendingFormData.logo) {
        const logoUploaded = await uploadLogoToAPI(pendingFormData.logo);
      }
      const selectedBank = bankDetails?.find(bank => bank.bank === pendingFormData.bankName);

      const payload = {
        id: selectedBank?.id,
        bank: (pendingFormData.bankName === "Other Bank" ? pendingFormData.otherBankName : pendingFormData.bankName) || "",
        accountNumber: pendingFormData.accountNumber || "",
        clearingNumber: pendingFormData.clearingNumber || "",
        ibanNumber: pendingFormData.ibanNumber || "",
        bicNumber: pendingFormData.bicNumber || "",
        incomeTax: selectedBank?.incomeTax || 0,
        code: otp,
      };

      const result = await saveBankDetails(payload);
      if (result.payload?.statusCode === 200) {
        await dispatch(updateUserOnboardingService({ actionType: 'IsSkipForNow', value: true })).unwrap();
        await dispatch(getUserOnboarding()).unwrap();
        await dispatch(fetchOrganization(formData.organizationName)).unwrap();
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
      setShowOTPModal(false);
      setPendingFormData(null);
      setOtpResetTrigger(!otpResetTrigger);
      loadBankDetails();

      onSubmit?.(pendingFormData);
    } catch (error) {
      console.error('Failed to save bank details:', error);
      setOtpResetTrigger(!otpResetTrigger);
    }
  };

  const handleOTPResend = () => {
    setOtpCounter(300);
    setOtpResetTrigger(!otpResetTrigger);
    handleGenerateOTP();
  };

  const isFieldPrefilled = (field: 'accountNumber' | 'clearingNumber' | 'ibanNumber' | 'bicNumber') => {
    const selectedBank = bankDetails?.find(bank => bank.bank === formData.bankName);
    if (!selectedBank) return false;

    switch (field) {
      case 'accountNumber':
        return !!selectedBank.accountNumber;
      case 'clearingNumber':
        return !!selectedBank.clearingNumber;
      case 'ibanNumber':
        return !!selectedBank.ibanNumber;
      case 'bicNumber':
        return !!selectedBank.bicNumber;
      default:
        return false;
    }
  };

  const hasApiBank = () => {
    return bankDetails?.some(bank => bank.bank === formData.bankName);
  };

  const findBankByIdOrName = (bankIdOrName: string) => {
    if (!bankDetails || !bankIdOrName) return null;

    let bank = bankDetails.find(bank =>
      bank.bank.toLowerCase() === bankIdOrName.toLowerCase()
    );

    if (!bank) {
      bank = bankDetails.find(bank => bank.id.toString() === bankIdOrName);
    }

    return bank;
  };



  const handleInputChange = (field: keyof InvoiceConfigData, value: string | File | null) => {
    const newFormData = {
      ...formData,
      [field]: value
    };

    setFormData(newFormData);
    onChange?.(newFormData);

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleBankSelection = (selectedBankName: string) => {
    const selectedBank = bankDetails?.find(bank => bank.bank === selectedBankName);

    let newFormData = {
      ...formData,
      bankName: selectedBankName
    };
    if (selectedBank) {
      if (isForeignBank(selectedBankName)) {
        newFormData = {
          ...newFormData,
          ibanNumber: selectedBank.ibanNumber || '',
          bicNumber: selectedBank.bicNumber || '',
          accountNumber: '',
          clearingNumber: ''
        };
      } else {
        newFormData = {
          ...newFormData,
          accountNumber: selectedBank.accountNumber || '',
          clearingNumber: selectedBank.clearingNumber || '',
          ibanNumber: '',
          bicNumber: ''
        };
      }
    } else {
      if (isForeignBank(selectedBankName)) {
        newFormData = {
          ...newFormData,
          accountNumber: '',
          clearingNumber: ''
        };
      } else {
        newFormData = {
          ...newFormData,
          ibanNumber: '',
          bicNumber: ''
        };
      }
    }

    setFormData(newFormData);
    onChange?.(newFormData);

    if (errors.bankName) {
      setErrors(prev => ({
        ...prev,
        bankName: undefined
      }));
    }
  };

  const handleLogoSelect = (file: File | null) => {
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          logo: t('INVALID_LOGO_FORMAT') as string
        }));
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          logo: t('LOGO_FILE_SIZE_EXCEEDED') as string
        }));
        return;
      }
    }

    handleInputChange('logo', file);
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof InvoiceConfigData, string>> = {};
    const rules = { ...defaultValidationRules, ...validationRules };

    Object.entries(rules).forEach(([field, rule]) => {
      const value = formData[field as keyof InvoiceConfigData];
      const error = rule(value as any);
      if (error) {
        newErrors[field as keyof InvoiceConfigData] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if(isGuestInvozioEmail(personalData.email)){
        toast.error(t('PLEASE_VERIFY_YOUR_EMAIL') as string)
        return
    }
    if (validateForm()) {
      // if (!isForeignBank(formData.bankName) && formData.clearingNumber) {
      //   setErrors(prev => ({
      //     ...prev,
      //     clearingNumber: 'This clearing number is not allowed. Please contact support.'
      //   }));
      //   return;
      // }

      setPendingFormData(formData);
      setShowConfirmModal(true);
    }
  };

  const handleConfirmSave = () => {
    setShowConfirmModal(false);
    handleGenerateOTP();
  };

  function handleRevoltClick(){
    window.open('https://www.revolut.com/en-IN/referral/?referral-code=kevindtxd%21JUN1-25-AR-H2')
  }

  return (
    <div className={`invoice-config ${className}`}>
      {/* {loading && (
        <div className="invoice-config__loading">
          <div className="loading-spinner"></div>
          <span>{t('LOADING_BANK_DETAILS')}</span>
        </div>
      )} */}

      <div className={`invoice-config__content invoice-config__content--with-preview`}>
        <div className="invoice-config__form">
          {/* Brand Name Field */}
          <FormField
            label={t("ORGANIZATION_NAME_OPTIONAL")}
            tooltipContent={t("ORGANIZATION_NAME_TOOLTIP")}
            error={errors.organizationName}
          >
            <input
              type="text"
              className={`form-input ${errors.organizationName ? 'form-input--error' : ''}`}
              placeholder={t("ORGANIZATION_NAME_PLACEHOLDER")}
              value={formData.organizationName}
              onChange={(e) => handleInputChange('organizationName', e.target.value)}
              disabled={readOnly}
            />
          </FormField>
          <div id="logo-upload-section">
            {/* Logo Upload Field */}
            <FormField
              label={t("UPLOAD_YOUR_LOGO")}
              tooltipContent={t("UPLOAD_YOUR_LOGO_TOOLTIP")}
              error={errors.logo}
            >
              <LogoUpload
                onLogoSelect={handleLogoSelect}
                selectedLogo={formData.logo}
                disabled={readOnly}
                maxSize={5}
                acceptedFormats={['jpeg', 'png', 'svg+xml']}
              />
            </FormField>
          </div>

          {/* Bank Name Field */}
          <FormField
            label={t("BANK_NAME")}
            tooltipContent={t("BANK_NAME_TOOLTIP")}
            error={errors.bankName}
            required
            helpText={t("BANK_NAME_HELP_TEXT")}
          >
            <select
              className={`form-select ${errors.bankName ? 'form-input--error' : ''}`}
              value={formData.bankName}
              onChange={(e) => handleBankSelection(e.target.value)}
              disabled={readOnly}
            >
              <option value="">{t("SELECT_A_BANK")}</option>
              <option value="Handelsbanken">{t('HANDELSBANKEN')}</option>
              <option value="Nordea social security number">{t('NORDIA_SOCIAL_SECURITY_NUMBER')}</option>
              <option value="Nordea Account number">{t('NORDEA_ACCOUNT_NUMBER')}</option>
              <option value="Revolut Bank">{t('REVOLUT_BANK')}</option>
              <option value="Swedbank">{t('SWEDBANK')}</option>
              <option value="SEB">{t('SEB')}</option>
              <option value="Foreign bank BIC / IBAN">{t('FOREIGN_BANK_BIC_IBAN')}</option>
              <option value="Other Bank">{t('OTHER_BANK')}</option>
              {/* Dynamic options from API */}
              {bankDetails?.map((bank) => {
                const predefinedBanks = [
                  "Handelsbanken", "Nordea social security number", "Nordea Account number",
                  "Revolut Bank", "Swedbank", "SEB", "Foreign bank BIC / IBAN", "Other Bank"
                ];
                if (!predefinedBanks.includes(bank.bank)) {
                  return (
                    <option key={bank.id} value={bank.bank}>
                      {bank.bank}
                    </option>
                  );
                }
                return null;
              })}
            </select>
                  <p className='py-0 my-2'>
            {t("CHOOSE_REVOLUT_GET_PAID_FAST")} <button className='sign-up-text' onClick={() => handleRevoltClick()}> {t("SIGN_UP_TEXT")} </button>  {t("FOR_A_NEW_ACCOUNT")}
          </p>
          {/* </div> */}

          </FormField>
          {/* Other Bank Name Field - Show when "Other Bank" is selected */}
          {formData.bankName.includes("Other Bank") && (
            <FormField
              label={t("OTHER_BANK_NAME")}
              tooltipContent={t("OTHER_BANK_NAME_TOOLTIP")}
              error={errors.otherBankName}
              required
            >
              <input
                type="text"
                className={`form-input ${errors.otherBankName ? 'form-input--error' : ''}`}
                placeholder="Your Bank Name Here"
                value={formData.otherBankName || ''}
                onChange={(e) => handleInputChange('otherBankName', e.target.value)}
                disabled={readOnly}
              />
            </FormField>
          )}

          {isForeignBank(formData.bankName) ? (
            <>
              <FormField
                label={t("IBAN_NUMBER")}
                tooltipContent={t("IBAN_NUMBER_TOOLTIP")}
                error={errors.ibanNumber}
                required
              >
                <input
                  type="text"
                  className={`form-input ${errors.ibanNumber ? 'form-input--error' : ''}`}
                  placeholder="IBAN Number"
                  value={formData.ibanNumber || ''}
                  onChange={(e) => handleInputChange('ibanNumber', e.target.value)}
                  disabled={readOnly}
                />
              </FormField>

              {/* BIC Number Field for Foreign Banks */}
              <FormField
                label={t("BIC_NUMBER")}
                tooltipContent={t("BIC_NUMBER_TOOLTIP")}
                error={errors.bicNumber}
                required
              >
                <input
                  type="text"
                  className={`form-input ${errors.bicNumber ? 'form-input--error' : ''}`}
                  placeholder="BIC Number"
                  value={formData.bicNumber || ''}
                  onChange={(e) => handleInputChange('bicNumber', e.target.value)}
                  disabled={readOnly}
                />
              </FormField>
            </>
          ) : (
            <>
              {/* Clearing Number Field for Swedish Banks */}
              <FormField
                label={t("CLEARING_NUMBER")}
                tooltipContent={t("CLEARING_NUMBER_TOOLTIP")}
                error={errors.clearingNumber}
                required
              >
                <input
                  type="text"
                  className={`form-input ${errors.clearingNumber ? 'form-input--error' : ''}`}
                  placeholder="e.g. 12345"
                  value={formData.clearingNumber}
                  onChange={(e) => handleInputChange('clearingNumber', e.target.value)}
                  maxLength={5}
                  disabled={readOnly}
                />
              </FormField>

              {/* Account Number Field for Swedish Banks */}
              <FormField
                label={t("ACCOUNT_NUMBER")}
                tooltipContent={t("ACCOUNT_NUMBER_TOOLTIP")}
                error={errors.accountNumber}
                required
              >
                <input
                  type="text"
                  className={`form-input ${errors.accountNumber ? 'form-input--error' : ''}`}
                  placeholder="e.g. 1234567890"
                  value={formData.accountNumber}
                  onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                  maxLength={10}
                  disabled={readOnly}
                />
              </FormField>
            </>
          )}

          {isForeignBank(formData.bankName) && (
            <div className="invoice-config__note">
              {/* <p>
                {t("NOTE_ACCOUNT_DETAILS")}
              </p> */}
            </div>
          )}

          {/* Disclaimer */}
          <div className="invoice-config__disclaimer">
            <svg className="invoice-config__warning-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" />
              <path d="M10 7v6M10 5h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <p>
              {t("BANK_DETAILS_DISCLAIMER_TEXT")}
            </p>
          </div>
          <div className="invoice-config__actions">
            <button
              className="invoice-config__submit-button mb-4"
              onClick={handleSubmit}
              type="button"
            >
              {t(submitButtonText)}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3.333 8h9.334M9.333 4.667L12.667 8l-3.334 3.333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>

        {showPreview && (
          <InvoicePreview
            className="invoice-config__preview"
            width='500px'
            brandName={formData.organizationName || "YOUR LOGO"}
            logoUrl={formData.logo ? URL.createObjectURL(formData.logo) : undefined}
          />
        )}
      </div>

      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSave}
        title={t("BANK_INFORMATION") as string}
        message={t('CONFIRM_BANK_DETAILS_MESSAGE') as string}
        confirmText={t("CONFIRM_AND_CONTINUE") as string}
        cancelText={t("NO") as string}
        loading={otpLoading}
      />

      {/* OTP Modal */}
      <OTPModal
        isOpen={showOTPModal}
        onClose={() => {
          setShowOTPModal(false);
          setOtpResetTrigger(!otpResetTrigger);
        }}
        onSubmit={handleOTPSubmit}
        onResend={handleOTPResend}
        loading={saveLoading}
        counter={otpCounter}
        setCounter={setOtpCounter}
        resetTrigger={otpResetTrigger}
      />
    </div>
  );
};
